// ==================================================
// IMPORTS
//
// Purpose:
// External libraries, API services,
// calendar components and modals.
// ==================================================
import React, { useEffect, useState } from "react";
import api from "../utils/api";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import { toast } from "react-toastify";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../styles/CalendarPage.css";
import DndScheduleModal from "../components/messages/DndScheduleModal";

// ==================================================
// CALENDAR CONFIGURATION
//
// Purpose:
// React Big Calendar setup.
// Drag-and-drop support.
// Date localization.
// ==================================================
const DnDCalendar = withDragAndDrop(Calendar);

const locales = {
    "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const CalendarPage = () => {
    // ==================================================
    // STATE MANAGEMENT
    //
    // Purpose:
    // Calendar data
    // Modal state
    // Staff scheduling
    // DND scheduling
    // User selections
    // ==================================================
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [newEventTitle, setNewEventTitle] = useState("");
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [modalEventId, setModalEventId] = useState(null);
    const [modalTitle, setModalTitle] = useState("");
    const [modalDate, setModalDate] = useState("");
    const [eventFilter, setEventFilter] = useState("all");
    const [alsoSaveToNotepad, setAlsoSaveToNotepad] = useState(true);
    const [alsoPromoteToReminder, setAlsoPromoteToReminder] = useState(true);
    const [staffList, setStaffList] = useState([]);
    const [assignedStaff, setAssignedStaff] = useState("");
    const [modalType, setModalType] = useState("note");
    const [modalTime, setModalTime] = useState("");

    const [showDndScheduleModal, setShowDndScheduleModal] = useState(false);
    const [showCreateDndSchedule, setShowCreateDndSchedule] = useState(false);
    const [savedDndSchedules, setSavedDndSchedules] = useState([]);
    const [editingDndScheduleId, setEditingDndScheduleId] = useState(null);

    const [dndScheduleName, setDndScheduleName] = useState("");
    const [dndStartTime, setDndStartTime] = useState("09:00");
    const [dndEndTime, setDndEndTime] = useState("10:00");
    const [dndStartDate, setDndStartDate] = useState("");
    const [dndEndDate, setDndEndDate] = useState("");
    const [dndRepeat, setDndRepeat] = useState("none");
    const [dndRepeatDays, setDndRepeatDays] = useState([]);
    const [dndVisibleTo, setDndVisibleTo] = useState(["admin", "director", "staff"]);
    const [dndNotes, setDndNotes] = useState("");
    const [sendInAppNotification, setSendInAppNotification] = useState(true);
    const [sendEmailNotification, setSendEmailNotification] = useState(false);
    const [useCenterTimezone, setUseCenterTimezone] = useState(true);
    const [currentStatus, setCurrentStatus] = useState(null);

    // ==================================================
    // DND BYPASS RULES
    //
    // Purpose:
    // Determines what notifications
    // can bypass staff blocks.
    // ==================================================
    const [dndBypassRules, setDndBypassRules] = useState({
        emergencyAlerts: true,
        pickupEmergencies: true,
        adminOverride: true,
        incidentReports: true,
    });


    // ==================================================
    // CENTER MANAGEMENT
    //
    // Purpose:
    // Retrieves active center
    // for multi-center support.
    // ==================================================
    const getCenterId = () => {
        const user = JSON.parse(localStorage.getItem("kuddleUser") || "{}");
        return user.currentCenterId || user.primaryCenterId || "";
    };

    // ==================================================
    // DATA LOADING
    //
    // Purpose:
    // Load calendar records
    // Load staff
    // Load schedules
    // Refresh events
    // ==================================================
    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get("/api/calendar", {
                    params: { centerId: getCenterId() },
                });

                const formatted = (res.data || []).map((e) => ({
                    ...e,
                    title: e.title,
                    start: new Date(e.eventDate),
                    end: new Date(e.eventDate),
                }));

                setEvents(formatted);
            } catch (err) {
                console.error("Calendar load failed:", err);
                setEvents([]);
            }
        };

        load();
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const refreshCalendar = async () => {
            try {
                const res = await api.get("/api/calendar", {
                    params: { centerId: getCenterId() },
                });

                const formatted = (res.data || []).map((e) => ({
                    ...e,
                    title: e.title,
                    start: new Date(e.eventDate),
                    end: new Date(e.eventDate),
                }));

                setEvents(formatted);
            } catch (err) {
                console.error("Calendar refresh failed:", err);
            }
        };

        window.addEventListener("kuddle:calendar-updated", refreshCalendar);

        return () => {
            window.removeEventListener("kuddle:calendar-updated", refreshCalendar);
        };
    }, []);

    useEffect(() => {
        const loadStaff = async () => {
            try {
                const res = await api.get("/api/users?role=staff");
                setStaffList(res.data || []);
            } catch (err) {
                console.error("Failed to load staff", err);
            }
        };

        loadStaff();
    }, []);

    useEffect(() => {
        const fetchFocusSchedules = async () => {
            try {
                const res = await fetch('/api/focus-schedules', {
                    headers: getAuthHeaders(),
                });

                if (!res.ok) return;

                const data = await res.json();
                setSavedDndSchedules(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Fetch focus schedules failed:', err);
            }
        };

        fetchFocusSchedules();

        window.addEventListener('kuddle:focus-schedules-updated', fetchFocusSchedules);

        return () => {
            window.removeEventListener('kuddle:focus-schedules-updated', fetchFocusSchedules);
        };
    }, []);

    // ==================================================
    // CALENDAR ACTIONS
    //
    // Purpose:
    // Create
    // Edit
    // Move
    // Delete
    // Synchronize notes/reminders
    // ==================================================
    const handleEventDrop = async ({ event, start }) => {
        const newDateIso = new Date(start).toISOString();

        // Update UI immediately so it does not bounce back first
        setEvents((prev) =>
            prev.map((e) =>
                e._id === event._id
                    ? { ...e, eventDate: newDateIso, start, end: start }
                    : e
            )
        );

        try {
            await api.put(`/api/calendar/${event._id}`, {
                eventDate: newDateIso,
            });

            if (event.fromNoteId) {
                await api.put(`/api/notes/${event.fromNoteId}`, {
                    scheduledAt: newDateIso,
                });
            }

            if (event.fromReminderId) {
                await api.put(`/api/reminders/${event.fromReminderId}`, {
                    remindAt: newDateIso,
                });

                window.dispatchEvent(new Event("kuddle:reminders-updated"));
            }

        } catch (err) {
            console.error("Failed to move event:", err);
            toast.error("Event move failed");

            // If backend fails, put it back
            setEvents((prev) =>
                prev.map((e) =>
                    e._id === event._id
                        ? { ...e, start: event.start, end: event.end, eventDate: event.eventDate }
                        : e
                )
            );
        }
    };

    const handleSave = async () => {
        const title = modalTitle.trim();
        if (!title) return;

        try {
            const eventDateIso =
                modalDate && modalTime
                    ? new Date(`${modalDate}T${modalTime}`).toISOString()
                    : new Date().toISOString();

            // =========================
            // ✅ CREATE FLOW
            // =========================
            if (modalMode === "add") {

                let noteId = "";
                let reminderId = "";

                // 1️⃣ CREATE NOTE
                try {
                    const noteRes = await api.post("/api/notes", {
                        content: title,
                        tags: ["#calendar"],
                        scheduledAt: eventDateIso,
                        centerId: getCenterId(),
                        createdBy: "admin",
                    });
                    noteId = noteRes.data?._id || "";
                } catch (err) {
                    console.error("Note save failed:", err);
                }

                // 2️⃣ CREATE REMINDER (NOW ALWAYS)
                try {
                    const reminderRes = await api.post("/api/reminders", {
                        message: title,
                        audience: "role",
                        target: "staff",
                        remindAt: eventDateIso,
                        centerId: getCenterId(),
                    });

                    reminderId =
                        reminderRes.data?.reminder?._id ||
                        reminderRes.data?._id ||
                        "";

                    window.dispatchEvent(new Event("kuddle:reminders-updated"));
                } catch (err) {
                    console.error("Reminder save failed:", err);
                }

                // 3️⃣ CREATE CALENDAR WITH LINKS
                const calendarRes = await api.post("/api/calendar", {
                    title,
                    description: title,
                    eventDate: eventDateIso,
                    type: modalType || "note",
                    assignedTo: assignedStaff || null,
                    centerId: getCenterId(),
                    createdBy: "admin",
                    fromNoteId: noteId,
                    fromReminderId: reminderId,
                });

                // 4️⃣ UPDATE UI
                const newEvent = {
                    ...calendarRes.data,
                    start: new Date(eventDateIso),
                    end: new Date(eventDateIso),
                    fromNoteId: noteId,
                    fromReminderId: reminderId,
                };

                setEvents((prev) => [...prev, newEvent]);


            }
            // =========================
            // ✅ UPDATE FLOW (FIXED)
            // =========================
            else if (modalMode === "edit") {

                const linkedEvent = events.find((e) => e._id === modalEventId);


                if (!linkedEvent) {
                    toast.error("Event not found");
                    return;
                }

                // 1️⃣ Update calendar FIRST (source of truth)
                const res = await api.put(`/api/calendar/${modalEventId}`, {
                    title,
                    description: title,
                    eventDate: eventDateIso,
                    type: modalType,
                    assignedTo: assignedStaff || null,
                });

                // 2️⃣ Update linked note
                if (linkedEvent.fromNoteId) {
                    try {
                        await api.put(`/api/notes/${linkedEvent.fromNoteId}`, {
                            content: title,
                            scheduledAt: eventDateIso,
                        });
                    } catch (err) {
                        console.warn("Note update failed (non-blocking)", err);
                    }
                }

                // 3️⃣ Update linked reminder
                if (linkedEvent.fromReminderId) {
                    try {
                        await api.put(`/api/reminders/${linkedEvent.fromReminderId}`, {
                            message: title,
                            remindAt: eventDateIso,
                        });

                        window.dispatchEvent(new Event("kuddle:reminders-updated"));
                    } catch (err) {
                        console.warn("Reminder update failed (non-blocking)", err);
                    }
                }

                // 4️⃣ Update UI
                setEvents((prev) =>
                    prev.map((e) =>
                        e._id === modalEventId
                            ? {
                                ...e,
                                ...res.data,
                                title,
                                start: new Date(eventDateIso),
                                end: new Date(eventDateIso),
                            }
                            : e
                    )
                );


            }

            setCalendarModalOpen(false);

        } catch (err) {
            console.error("Save failed:", err.response?.data || err);
            toast.error(err.response?.data?.message || "Save failed");
        }
    };

    const handleDelete = async () => {
        try {
            const linkedEvent = events.find((e) => e._id === modalEventId);

            // 1️⃣ Delete calendar FIRST (source of truth)
            await api.delete(`/api/calendar/${modalEventId}`);

            // 2️⃣ Delete linked note (if exists)
            if (linkedEvent?.fromNoteId) {
                try {
                    await api.delete(`/api/notes/${linkedEvent.fromNoteId}`);
                } catch (err) {
                    console.warn("Note delete failed (non-blocking)", err);
                }
            }

            // 3️⃣ Delete linked reminder (if exists)
            if (linkedEvent?.fromReminderId) {
                try {
                    await api.delete(`/api/reminders/${linkedEvent.fromReminderId}`);
                    window.dispatchEvent(new Event("kuddle:reminders-updated"));
                } catch (err) {
                    console.warn("Reminder delete failed (non-blocking)", err);
                }
            }

            // 4️⃣ Update UI
            setEvents((prev) => prev.filter((e) => e._id !== modalEventId));

            setCalendarModalOpen(false);


        } catch (err) {
            console.error("Calendar delete failed:", err);
            toast.error("Delete failed");
        }
    };

    // ==================================================
    // UTILITY FUNCTIONS
    //
    // Purpose:
    // Authentication helpers
    // Time formatting helpers
    // ==================================================
    const getAuthHeaders = () => {
        const token =
            sessionStorage.getItem("kuddleToken") ||
            localStorage.getItem("kuddleToken");

        return {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getRoundedTime = (minutesToAdd = 0) => {
        const date = new Date();
        date.setMinutes(date.getMinutes() + minutesToAdd);

        const minutes = date.getMinutes();
        const roundedMinutes = Math.ceil(minutes / 5) * 5;

        date.setMinutes(roundedMinutes);
        date.setSeconds(0);

        return date.toTimeString().slice(0, 5);
    };

    // ==================================================
    // CALENDAR RENDER UI
    //
    // Purpose:
    // Calendar display
    // Calendar filters
    // Event modals
    // Staff block scheduler
    // ==================================================
    return (
        <div className="calendar-page" style={{ padding: 20 }}>
            <div className="page-header-row">
                <div>
                    <h1 style={{ marginBottom: 4 }}>📅 Calendar</h1>
                    <p>Manage events, notes, reminders, and alerts.</p>
                </div>

            </div>

            <div className="calendar-jump-row">
                <select
                    value={currentDate.getMonth()}
                    onChange={(e) =>
                        setCurrentDate(
                            new Date(currentDate.getFullYear(), Number(e.target.value), 1)
                        )
                    }
                >
                    {Array.from({ length: 12 }).map((_, i) => (
                        <option key={i} value={i}>
                            {new Date(currentDate.getFullYear(), i).toLocaleString("default", {
                                month: "long",
                            })}
                        </option>
                    ))}
                </select>

                <select
                    value={currentDate.getFullYear()}
                    onChange={(e) =>
                        setCurrentDate(new Date(Number(e.target.value), currentDate.getMonth(), 1))
                    }
                >
                    {Array.from({ length: 12 }).map((_, i) => {
                        const y = new Date().getFullYear() - 2 + i;
                        return (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        );
                    })}
                </select>
            </div>

            {/* ----- Calendar Filters ----- */}
            <div className="calendar-filter-row">
                <button onClick={() => setEventFilter("all")}>All</button>
                <button onClick={() => setEventFilter("note")}>Notes</button>
                <button onClick={() => setEventFilter("reminder")}>Reminders</button>
                <button onClick={() => setEventFilter("alert")}>Alerts</button>
                <button
                    type="button"
                    className={`calendar-filter-btn ${eventFilter === 'staff_block' ? 'active' : ''}`}
                    onClick={() => setEventFilter('staff_block')}
                >
                    Staff Blocks
                </button>
                <button
                    type="button"
                    className="calendar-staff-block-btn"
                    onClick={() => {
                        setEditingDndScheduleId(null);

                        setDndScheduleName('');
                        setDndStartDate('');
                        setDndEndDate('');
                        setDndStartTime(getRoundedTime(0));
                        setDndEndTime(getRoundedTime(30));
                        setDndRepeat('none');
                        setDndRepeatDays([]);
                        setDndVisibleTo(['admin', 'director', 'staff']);
                        setDndNotes('');

                        setSendInAppNotification(true);
                        setSendEmailNotification(false);

                        setShowCreateDndSchedule(true);
                        setShowDndScheduleModal(true);
                    }}
                >
                    🕒 Schedule Staff Block
                </button>
            </div>

            <DnDCalendar
                localizer={localizer}
                events={
                    (eventFilter === "all"
                        ? events.filter((e) => e.status !== "completed")
                        : events.filter((e) => e.type === eventFilter && e.status !== "completed")
                    ).sort((a, b) => {
                        const order = {
                            alert: 1,
                            reminder: 2,
                            note: 3,
                            staff_block: 4,
                            event: 5,
                        };

                        return (order[a.type] || 99) - (order[b.type] || 99);
                    })
                }
                startAccessor="start"
                endAccessor="end"
                date={currentDate}
                onNavigate={(date) => setCurrentDate(date)}
                selectable
                onSelectSlot={(slotInfo) => {
                    setModalMode("add");
                    setModalEventId(null);
                    setModalTitle("");
                    setModalDate(new Date(slotInfo.start).toISOString().slice(0, 10));
                    setModalTime("09:00");
                    setCalendarModalOpen(true);
                }}

                onSelectEvent={async (event) => {
                    if (event.type === "staff_block") {
                        const eventTimePassed = new Date(event.start) < new Date();

                        const matchingSchedule = savedDndSchedules.find(
                            (schedule) =>
                                String(schedule._id || schedule.id) === String(event.linkedScheduleId)
                        );

                        if (eventTimePassed || !matchingSchedule || matchingSchedule.enabled === false) {
                            await api.put(`/api/calendar/${event._id}`, {
                                status: "completed",
                            });

                            setEvents((prev) => prev.filter((e) => e._id !== event._id));
                            return;
                        }

                        setEditingDndScheduleId(event.linkedScheduleId || null);
                        setShowCreateDndSchedule(true);
                        setShowDndScheduleModal(true);
                        return;
                    }

                    setModalMode("edit");
                    setModalEventId(event._id);
                    setModalTitle(event.title || "");

                    const selectedDate = new Date(event.start);

                    setModalDate(selectedDate.toISOString().slice(0, 10));
                    setModalTime(selectedDate.toTimeString().slice(0, 5));
                    setModalType(event.type || "note");
                    setAlsoSaveToNotepad(false);
                    setAlsoPromoteToReminder(false);
                    setAssignedStaff(event.assignedTo || "");
                    setCalendarModalOpen(true);
                }}

                onEventDrop={handleEventDrop}
                style={{
                    height: "calc(100vh - 220px)",
                    background: "var(--card-bg)",
                    borderRadius: 16,
                    padding: 10,
                }}
                eventPropGetter={(event) => {
                    let backgroundColor = "#ede9fe";
                    let textColor = "#111";

                    if (event.type === "alert") {
                        backgroundColor = "#fee2e2";
                        textColor = "#7f1d1d";
                    }

                    if (event.type === "reminder") {
                        backgroundColor = "#fce7f3";
                        textColor = "#831843";
                    }

                    if (event.type === "note") {
                        backgroundColor = "#ede9fe";
                        textColor = "#3b0764";
                    }

                    if (event.type === "staff_block") {
                        backgroundColor = "#7c3aed";
                        textColor = "#fff";
                    }

                    return {
                        style: {
                            backgroundColor,
                            color: textColor,
                            borderRadius: "8px",
                            border: "none",
                            fontWeight: 600,
                            padding: "2px 6px",
                        },
                    };
                }}
            />

            {calendarModalOpen && (
                <div className="modal-overlay" onClick={() => setCalendarModalOpen(false)}>
                    <div
                        className="modal-card calendar-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="modal-title">
                            {modalMode === "add"
                                ? modalType === "note"
                                    ? "Add Note"
                                    : modalType === "reminder"
                                        ? "Add Reminder"
                                        : modalType === "alert"
                                            ? "Add Alert"
                                            : "Add Event"
                                : modalType === "note"
                                    ? "Edit Note"
                                    : modalType === "reminder"
                                        ? "Edit Reminder"
                                        : modalType === "alert"
                                            ? "Edit Alert"
                                            : "Edit Event"}
                        </h2>

                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                value={modalTitle}
                                onChange={(e) => setModalTitle(e.target.value)}
                                placeholder="Enter event title"
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Date & Time</label>
                            <div className="calendar-date-time-row">
                                <input
                                    type="date"
                                    value={modalDate}
                                    onChange={(e) => setModalDate(e.target.value)}
                                    className="form-input"
                                />

                                <select
                                    value={modalTime}
                                    onChange={(e) => setModalTime(e.target.value)}
                                    className="form-input calendar-time-select"
                                >
                                    <option value="">Select time</option>
                                    <option value="07:00">7:00 AM</option>
                                    <option value="07:30">7:30 AM</option>
                                    <option value="08:00">8:00 AM</option>
                                    <option value="08:30">8:30 AM</option>
                                    <option value="09:00">9:00 AM</option>
                                    <option value="09:30">9:30 AM</option>
                                    <option value="10:00">10:00 AM</option>
                                    <option value="10:30">10:30 AM</option>
                                    <option value="11:00">11:00 AM</option>
                                    <option value="11:30">11:30 AM</option>
                                    <option value="12:00">12:00 PM</option>
                                    <option value="12:30">12:30 PM</option>
                                    <option value="13:00">1:00 PM</option>
                                    <option value="13:30">1:30 PM</option>
                                    <option value="14:00">2:00 PM</option>
                                    <option value="14:30">2:30 PM</option>
                                    <option value="15:00">3:00 PM</option>
                                    <option value="15:30">3:30 PM</option>
                                    <option value="16:00">4:00 PM</option>
                                    <option value="16:30">4:30 PM</option>
                                    <option value="17:00">5:00 PM</option>
                                    <option value="17:30">5:30 PM</option>
                                    <option value="18:00">6:00 PM</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Assign to Staff</label>
                            <select
                                value={assignedStaff}
                                onChange={(e) => setAssignedStaff(e.target.value)}
                                className="form-input"
                            >
                                <option value="">None</option>
                                {staffList.map((s) => (
                                    <option key={s._id} value={s._id}>
                                        {s.name || s.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Event Type</label>
                            <select
                                value={modalType}
                                onChange={(e) => setModalType(e.target.value)}
                                className="form-input"
                            >
                                <option value="note">Note</option>
                                <option value="reminder">Reminder</option>
                                <option value="alert">Alert</option>
                            </select>
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input type="checkbox" checked={true} disabled />
                                Save to Calendar, Notepad, and Reminders (Auto)
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={modalType === "alert"}
                                    onChange={() =>
                                        setModalType(modalType === "alert" ? "note" : "alert")
                                    }
                                />
                                Mark as Alert
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button type="button" className="btn-primary" onClick={handleSave}>
                                {modalMode === "add" ? "Save Event" : "Update Event"}
                            </button>

                            {modalMode === "edit" && (
                                <button type="button" className="btn-danger" onClick={handleDelete}>
                                    Delete
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => setCalendarModalOpen(false)}
                            >
                                Cancel
                            </button>


                        </div>
                    </div>
                </div>
            )}
            <DndScheduleModal
                showDndScheduleModal={showDndScheduleModal}
                setShowDndScheduleModal={setShowDndScheduleModal}
                savedDndSchedules={savedDndSchedules}
                setSavedDndSchedules={setSavedDndSchedules}
                getAuthHeaders={getAuthHeaders}
                setDndScheduleName={setDndScheduleName}
                setDndStartTime={setDndStartTime}
                setDndEndTime={setDndEndTime}
                setDndStartDate={setDndStartDate}
                setDndEndDate={setDndEndDate}
                setDndRepeat={setDndRepeat}
                setDndRepeatDays={setDndRepeatDays}
                setDndVisibleTo={setDndVisibleTo}
                setDndNotes={setDndNotes}
                setSendInAppNotification={setSendInAppNotification}
                setSendEmailNotification={setSendEmailNotification}
                setEditingDndScheduleId={setEditingDndScheduleId}
                editingDndScheduleId={editingDndScheduleId}
                showCreateDndSchedule={showCreateDndSchedule}
                setShowCreateDndSchedule={setShowCreateDndSchedule}
                dndScheduleName={dndScheduleName}
                dndStartTime={dndStartTime}
                dndEndTime={dndEndTime}
                dndStartDate={dndStartDate}
                dndEndDate={dndEndDate}
                dndRepeat={dndRepeat}
                dndRepeatDays={dndRepeatDays}
                dndVisibleTo={dndVisibleTo}
                dndNotes={dndNotes}
                sendInAppNotification={sendInAppNotification}
                sendEmailNotification={sendEmailNotification}
                useCenterTimezone={useCenterTimezone}
                dndBypassRules={dndBypassRules}
                formatTypedTime={(value) => value}
                setCurrentStatus={setCurrentStatus}
            />
        </div>
    );
};

export default CalendarPage;